import { HttpErrorResponse, HttpParams, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import moment from 'moment';
import {
    BehaviorSubject,
    catchError,
    concat,
    EMPTY,
    exhaustMap,
    filter,
    map,
    Observable,
    of,
    Subscriber,
    switchMap,
    takeUntil,
    takeWhile,
    throwError,
    timer
} from 'rxjs';

import { SnackbarService } from '../../_core/services/snackbar.service';
import { TownContextService } from '../../_core/services/town-context.service';
import {
    getBankWithExpirationDate,
    getExternalAppId,
    getOwnTown,
    getTown,
    getUserId,
    setBankWithExpirationDate,
} from '../../_core/utilities/localstorage.util';
import { BankInfoDTO } from '../dto/bank-info.dto';
import { CampImprovementDTO, CampImprovementUpdateDTO } from '../dto/camping-group.dto';
import { CellDTO } from '../dto/cell.dto';
import { CitizenDTO, HeroSkillLevelsDTO } from '../dto/citizen.dto';
import { CitizenInfoDTO } from '../dto/citizen-info.dto';
import { ExternalToolsUpdateJobStateDTO } from '../dto/external-tools-update-state.dto';
import { RuinDTO } from '../dto/ruin.dto';
import { SeasonDTO } from '../dto/season.dto';
import { SeasonPhaseDTO } from '../dto/season-phase.dto';
import { TownDTO } from '../dto/town.dto';
import { TownListItemDTO } from '../dto/town-list-item.dto';
import { TownListPageResultDTO, TownListQuery } from '../dto/town-list-page.dto';
import { UpdateInfoDTO } from '../dto/update-info.dto';
import { TownListMapper } from '../mapper/town-list-item.mapper';
import { dtoToModelArray } from '../types/_common.class';
import { ToolsToUpdate, TownState, TownTypeId } from '../types/_types';
import { BankInfo } from '../types/bank-info.class';
import { Cell } from '../types/cell.class';
import { Citizen } from '../types/citizen.class';
import { CitizenInfo } from '../types/citizen-info.class';
import { HeroSkillLevels } from '../types/hero-skill-levels';
import { Ruin } from '../types/ruin.class';
import { Town } from '../types/town.class';
import { TownDetails } from '../types/town-details.class';
import { TownListPageResult } from '../types/town-list-item.model';
import { UpdateInfo } from '../types/update-info.class';
import { GlobalService } from './_global.service';

@Injectable({ providedIn: 'root' })
export class TownService extends GlobalService {
    private snackbar: SnackbarService = inject(SnackbarService);
    private readonly town_context: TownContextService = inject(TownContextService);

    /** La locale */
    private readonly locale: string = moment.locale();

    /** Source unique du citoyen du joueur courant, partagée entre le menu compte et la liste des
     *  citoyens (cf. docs/superpowers/specs/2026-08-05-menu-compte-header-design.md). */
    private readonly my_citizen_subject: BehaviorSubject<Citizen | null> = new BehaviorSubject<Citizen | null>(null);
    /** Le citoyen du joueur courant, tel que publié en dernier par n'importe quel consommateur. */
    public readonly myCitizen$: Observable<Citizen | null> = this.my_citizen_subject.asObservable();

    /** Publie une nouvelle version du citoyen du joueur courant : à appeler après tout fetch ou toute mutation réussie le concernant. */
    public publishMyCitizen(citizen: Citizen): void {
        this.my_citizen_subject.next(citizen);
    }

    /** Recharge le citoyen du joueur courant depuis le serveur et le republie (ex : après une mise à jour des outils externes). */
    public refreshMyCitizen(): void {
        const user_id: number | null = getUserId();
        if (!user_id) return;
        this.getCitizen(user_id).subscribe({
            next: (citizen: Citizen) => this.publishMyCitizen(citizen)
        });
    }

    /**
     * Récupère les informations de la banque
     *
     * @returns {Observable<BankInfo>}
     */
    public getBank(force?: boolean): Observable<BankInfo> {
        return new Observable((sub: Subscriber<BankInfo>) => {
            // Mode observateur : banque d'une ville tierce, en lecture pure côté serveur (?townId=),
            // sans passer par le cache localStorage (non indexé par ville) pour ne pas le polluer.
            if (this.town_context.isReadonly()) {
                super.get<BankInfoDTO>(this.API_URL + `/Fetcher/bank?townId=${getTown()?.town_id}`)
                    .subscribe({
                        next: (response: HttpResponse<BankInfoDTO>) => {
                            sub.next(new BankInfo(response.body));
                        },
                        error: (error: HttpErrorResponse) => {
                            sub.error(error);
                        }
                    });
                return;
            }
            const saved_bank: BankInfo | undefined = getBankWithExpirationDate();
            if (saved_bank && saved_bank.items.length > 0 && !force) {
                sub.next(saved_bank);
            } else {
                super.get<BankInfoDTO>(this.API_URL + '/Fetcher/bank')
                    .subscribe({
                        next: (response: HttpResponse<BankInfoDTO>) => {
                            const bank: BankInfo = new BankInfo(response.body);
                            setBankWithExpirationDate(bank);
                            sub.next(bank);
                        },
                        error: (error: HttpErrorResponse) => {
                            sub.error(error);
                        }
                    });
            }
        });
    }

    /** Intervalle d'interrogation de l'avancement d'une mise à jour des outils externes */
    private static readonly UPDATE_POLLING_INTERVAL_MS: number = 800;
    /** Au-delà, le suivi s'arrête ; le traitement, lui, continue côté serveur */
    private static readonly UPDATE_TIMEOUT_MS: number = 120000;

    /**
     * Met à jour les outils externes et émet l'avancement au fur et à mesure. Le serveur exécute le
     * travail en tâche de fond : on lance, puis on interroge son état jusqu'à la fin.
     */
    public updateExternalTools(): Observable<ExternalToolsUpdateJobStateDTO> {
        // Depuis le site, le bouton ne met plus à jour que MyHordes Optimizer. La synchronisation
        // de Gest'Hordes et Fata Morgana reste le rôle de l'extension, depuis les pages du jeu.
        // BigBroth'Hordes ne fonctionne plus ; l'outil reste connu au cas où son développeur
        // reprendrait le projet.
        const tools_to_update: ToolsToUpdate = {
            isBigBrothHordes: 'none',
            isFataMorgana: 'none',
            isGestHordes: 'none',
            isMyHordesOptimizer: 'api'
        };

        // Toujours la ville du joueur : c'est elle que l'API MyHordes renvoie pour son compte, même
        // s'il en observe une autre.
        const own_town: TownDetails | null = getOwnTown();
        const town_details: {
            townX: number,
            townY: number,
            isChaos: boolean,
            townId: number
        } = {
            townX: own_town?.town_x || 0,
            townY: own_town?.town_y || 0,
            isChaos: own_town?.is_chaos || false,
            townId: own_town?.town_id || 0
        };

        return super.post<ExternalToolsUpdateJobStateDTO>(
            this.API_URL + `/ExternalTools/Update/Start?userKey=${getExternalAppId()}&userId=${getUserId()}`,
            JSON.stringify({
                map: { toolsToUpdate: tools_to_update },
                townDetails: town_details
            }),
            true)
            .pipe(
                catchError((error: HttpErrorResponse) => {
                    // 409 : une mise à jour du même joueur tourne déjà (double clic, second onglet).
                    // Ce n'est pas une erreur, on suit celle qui est en cours.
                    if (error.status === HttpStatusCode.Conflict && error.error) {
                        return of(error.error as ExternalToolsUpdateJobStateDTO);
                    }
                    return throwError(() => error);
                }),
                switchMap((initial_state: ExternalToolsUpdateJobStateDTO) => concat(
                    of(initial_state),
                    this.followExternalToolsUpdate(initial_state.jobId)
                ))
            );
    }

    /** Émet chaque état du lancement suivi, puis se termine sur le dernier */
    private followExternalToolsUpdate(job_id: string): Observable<ExternalToolsUpdateJobStateDTO> {
        return timer(TownService.UPDATE_POLLING_INTERVAL_MS, TownService.UPDATE_POLLING_INTERVAL_MS)
            .pipe(
                // exhaustMap (pas switchMap) : si une interrogation n'a pas fini avant le tick suivant
                // (ex. rafraîchissement du token imbriqué plus lent que l'intervalle), on ignore ce tick
                // au lieu d'annuler la requête en cours — sinon elle ne finit jamais et boucle en échec.
                // Une interrogation en échec (coupure réseau, redémarrage de l'API) est ignorée :
                // le traitement continue côté serveur, la suivante reprendra le suivi.
                exhaustMap(() => this.getExternalToolsUpdateStatus().pipe(catchError(() => EMPTY))),
                // Un état sans lancement connu, ou portant un autre identifiant, n'est pas le nôtre :
                // le prendre pour argent comptant se lirait comme une fin de traitement réussie.
                filter((state: ExternalToolsUpdateJobStateDTO) => state.jobId === job_id),
                takeWhile((state: ExternalToolsUpdateJobStateDTO) => state.isRunning, true),
                takeUntil(timer(TownService.UPDATE_TIMEOUT_MS))
            );
    }

    private getExternalToolsUpdateStatus(): Observable<ExternalToolsUpdateJobStateDTO> {
        return super.get<ExternalToolsUpdateJobStateDTO>(
            this.API_URL + `/ExternalTools/Update/Status?userKey=${getExternalAppId()}&userId=${getUserId()}`,
            true,
            undefined,
            true)
            .pipe(map((response: HttpResponse<ExternalToolsUpdateJobStateDTO>) => response.body as ExternalToolsUpdateJobStateDTO));
    }

    public getTownRuins(): Observable<Ruin[]> {
        return new Observable((sub: Subscriber<Ruin[]>) => {

            super.get<RuinDTO[]>(this.API_URL + `/Fetcher/ruins?townId=${getTown()?.town_id}`)
                .subscribe({
                    next: (response: HttpResponse<RuinDTO[]>) => {
                        const ruins: Ruin[] = dtoToModelArray(Ruin, response.body).sort((a: Ruin, b: Ruin) => {
                            if (a.label[this.locale].localeCompare(b.label[this.locale]) < 0) {
                                return -1;
                            }
                            if (a.label[this.locale].localeCompare(b.label[this.locale]) > 0) {
                                return 1;
                            }
                            return 0;
                        });

                        sub.next(ruins);
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    /**
     * Récupère la liste des citoyens
     *
     * @returns {Observable<CitizenInfo>}
     */
    public getCitizens(): Observable<CitizenInfo> {
        return new Observable((sub: Subscriber<CitizenInfo>) => {
            super.get<CitizenInfoDTO>(this.API_URL + `/Fetcher/citizens?townId=${getTown()?.town_id}&userId=${getUserId()}`)
                .subscribe({
                    next: (response: HttpResponse<CitizenInfoDTO>) => {
                        sub.next(new CitizenInfo(response.body));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    /**
     * Récupère les informations d'un unique citoyen de la ville du joueur, même quand il en observe une autre
     *
     * @param {number} id
     */
    public getCitizen(id: number): Observable<Citizen> {
        return new Observable((sub: Subscriber<Citizen>) => {
            super.get<CitizenDTO>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${id}`)
                .subscribe({
                    next: (response: HttpResponse<CitizenDTO>) => {
                        sub.next(new Citizen(response.body || undefined));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public getMap(): Observable<Town> {
        return new Observable((sub: Subscriber<Town>) => {
            super.get<TownDTO>(this.API_URL + `/Fetcher/map?townId=${getTown()?.town_id}`)
                .subscribe({
                    next: (response: HttpResponse<TownDTO>) => {
                        sub.next(new Town(response.body));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public updateBag(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/Bag?townId=${getOwnTown()?.town_id}`, JSON.stringify(citizen.toCitizenBagDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public updateChest(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/Chest?townId=${getOwnTown()?.town_id}`, JSON.stringify(citizen.toCitizenChestDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public updateStatus(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/Status?townId=${getOwnTown()?.town_id}`, JSON.stringify(citizen.toCitizenStatusDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public updateHome(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/home?townId=${getOwnTown()?.town_id}`, JSON.stringify(citizen.toCitizenHomeDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public updateHeroicActions(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/HeroicActions?townId=${getOwnTown()?.town_id}`, JSON.stringify(citizen.toCitizenHeroicActionsDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public saveCell(cell: Cell): Observable<Cell> {
        return new Observable((sub: Subscriber<Cell>) => {
            super.post<CellDTO>(this.API_URL + `/Map/cell?townid=${getTown()?.town_id}&userId=${getUserId()}`, JSON.stringify(cell.toSaveCellDTO()))
                .subscribe({
                    next: (response: CellDTO) => {
                        this.snackbar.successSnackbar($localize`La cellule a bien été modifiée`);
                        sub.next(new Cell(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    /** Aménagement de la zone pour le camping : ajouts du jour, ou niveau constaté. */
    public saveCampImprovement(update: CampImprovementUpdateDTO): Observable<CampImprovementDTO> {
        return new Observable((sub: Subscriber<CampImprovementDTO>) => {
            super.post<CampImprovementDTO>(this.API_URL + `/Map/Cell/CampImprovement?townId=${getTown()?.town_id}`, JSON.stringify(update))
                .subscribe({
                    next: (response: CampImprovementDTO) => {
                        sub.next(response);
                        sub.complete();
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    /**
     * Met à jour quelques champs de la maison d'un citoyen ; les champs absents ne sont pas touchés côté API.
     * @param {number} citizen_id Identifiant du citoyen.
     * @param {Record<string, number | boolean>} fields Valeurs par clé `HomeEnum`.
     * @returns {Observable<UpdateInfo>} Information de mise à jour.
     */
    public updateHomeFields(citizen_id: number, fields: Record<string, number | boolean>): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/ExternalTools/home?townId=${getOwnTown()?.town_id}`, JSON.stringify({ userId: citizen_id, home: fields }))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public addDailyAction(citizen: Citizen, actionKey: string, day?: number): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen.id}/dailyAction/${encodeURIComponent(actionKey)}?day=${day ?? getOwnTown()?.day}`)
                .subscribe({
                    next: (update_info: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(update_info));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public removeDailyAction(citizen: Citizen, actionKey: string, day?: number): Observable<void> {
        return new Observable((sub: Subscriber<void>) => {
            super.delete(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen.id}/dailyAction/${encodeURIComponent(actionKey)}?day=${day ?? getOwnTown()?.day}`)
                .subscribe({
                    next: () => {
                        sub.next();
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    public saveChamanicDetails(citizen: Citizen): Observable<UpdateInfo> {
        return new Observable((sub: Subscriber<UpdateInfo>) => {
            super.post<UpdateInfoDTO>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen.id}/chamanicDetail`, JSON.stringify(citizen.chamanic_detail.modelToDto()))
                .subscribe({
                    next: (response: UpdateInfoDTO) => {
                        sub.next(new UpdateInfo(response));
                    },
                    error: (error: HttpErrorResponse) => {
                        sub.error(error);
                    }
                });
        });
    }

    /** Campings déjà effectués par un citoyen de la ville. */
    public updateCampingCount(citizen_id: number, camping_count: number): Observable<void> {
        return super.post<void>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen_id}/campingCount`, JSON.stringify({ campingCount: camping_count }))
            .pipe(map((): void => undefined));
    }

    /** Chaussures équipées (déclaration manuelle) d'un citoyen de la ville. */
    public updateShoesEquipped(citizen_id: number, shoes_equipped: boolean): Observable<void> {
        return super.post<void>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen_id}/shoesEquipped`, JSON.stringify({ shoesEquipped: shoes_equipped }))
            .pipe(map((): void => undefined));
    }

    /** Niveaux des compétences héroïques d'un citoyen de la ville. */
    public updateHeroSkills(citizen_id: number, levels: HeroSkillLevels): Observable<void> {
        const dto: HeroSkillLevelsDTO = { s: levels.s, u: levels.u, p: levels.p, e: levels.e, r: levels.r };
        return super.post<void>(this.API_URL + `/town/${getOwnTown()?.town_id}/user/${citizen_id}/heroSkills`, JSON.stringify(dto))
            .pipe(map((): void => undefined));
    }

    public getSeasons(): Observable<SeasonDTO[]> {
        return super.get<SeasonDTO[]>(`${this.API_URL}/town/seasons`, false)
            .pipe(map((response: HttpResponse<SeasonDTO[]>) => response.body ?? []));
    }

    public getSeasonPhases(): Observable<SeasonPhaseDTO[]> {
        return super.get<SeasonPhaseDTO[]>(`${this.API_URL}/town/season-phases`, false)
            .pipe(map((response: HttpResponse<SeasonPhaseDTO[]>) => response.body ?? []));
    }

    public getTownsPaged(query: TownListQuery): Observable<TownListPageResult> {
        let params: HttpParams = new HttpParams()
            .set('page', String(query.page))
            .set('pageSize', String(query.pageSize));
        if (query.season) {
            params = params.set('season', String(query.season));
        }
        if (query.phase) {
            params = params.set('phase', query.phase);
        }
        if (query.playerId) {
            params = params.set('playerId', String(query.playerId));
        }
        if (query.sortColumn) {
            params = params.set('sortColumn', query.sortColumn);
        }
        if (query.sortDirection) {
            params = params.set('sortDirection', query.sortDirection);
        }
        if (query.name) {
            params = params.set('name', query.name);
        }
        if (query.id) {
            params = params.set('id', query.id);
        }
        (query.types ?? []).forEach((type: TownTypeId) => params = params.append('types', type));
        (query.languages ?? []).forEach((language: string) => params = params.append('languages', language));
        (query.states ?? []).forEach((state: TownState) => params = params.append('states', state));

        return super.get<TownListPageResultDTO>(`${this.API_URL}/town/list`, false, params)
            .pipe(map((response: HttpResponse<TownListPageResultDTO>) => {
                const body: TownListPageResultDTO | null = response.body;
                return {
                    items: (body?.items ?? []).map((dto: TownListItemDTO) => TownListMapper.dtoToModel(dto)),
                    totalCount: body?.totalCount ?? 0,
                    availableTypes: body?.availableTypes ?? [],
                    availableLanguages: body?.availableLanguages ?? [],
                };
            }));
    }
}

